import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Alert,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
  BookOnline as BookingsIcon,
  CreditCard as SubscriptionIcon,
  Settings as SettingsIcon,
  HelpOutline as SupportIcon,
  Menu as MenuIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  Warning as WarningIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

// Tab components
import ProfileTab from './dashboard/ProfileTab';
import ScheduleTab from './dashboard/ScheduleTab';
import BookingsTab from './dashboard/BookingsTab';
import SettingsTab from './dashboard/SettingsTab';
import SupportTab from './dashboard/SupportTab';
import SubscriptionTab from './dashboard/SubscriptionTab';

// Drawer components
import DashboardDrawer from './dashboard/drawer/DashboardDrawer';
import ProfileInfoModal from './dashboard/ProfileInfoModal';
import QRCodeModal from './dashboard/profile/QRCodeModal';
import SubscriptionExpiryModal from './dashboard/SubscriptionExpiryModal';

const DRAWER_WIDTH = 280;

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileInfoOpen, setProfileInfoOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [targetSection, setTargetSection] = useState(null);
  const [centralServices, setCentralServices] = useState([]);
  
  const { currentUser, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation();

  // Redirect clients to auto-redirect route for proper handling
  useEffect(() => {
    if (userProfile) {
      // Only redirect if the user is explicitly a client
      if (userProfile.role === 'client') {
        navigate('/my-dashboard', { replace: true });
        return;
      }
    }
  }, [userProfile, navigate]);

  const tabs = [
    { id: 0, label: t('dashboard.myProfile'), icon: PersonIcon },
    { id: 1, label: t('dashboard.mySchedule'), icon: CalendarIcon },
    { id: 2, label: t('dashboard.myBookings'), icon: BookingsIcon },
    { id: 3, label: t('dashboard.subscription'), icon: SubscriptionIcon },
    { id: 4, label: t('dashboard.settings'), icon: SettingsIcon },
    { id: 5, label: t('dashboard.support'), icon: SupportIcon },
  ];

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }

    // Handle payment parameters
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      setSuccess(t('dashboard.paymentSuccess'));
      setCurrentTab(3);
      setSearchParams({});
    } else if (paymentStatus === 'cancelled') {
      setError(t('dashboard.paymentCancelled'));
      setCurrentTab(3);
      setSearchParams({});
    }
  }, [currentUser, navigate, searchParams, setSearchParams]);

  // Handle tab parameter from URL (for modal redirects)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 5) {
        setCurrentTab(tabIndex);
        // Clear the tab parameter from URL
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('tab');
        setSearchParams(newSearchParams);
      }
    }
  }, [searchParams, setSearchParams]);

  // Synchronize central services with user profile
  useEffect(() => {
    if (userProfile?.services) {
      setCentralServices(userProfile.services);
    }
  }, [userProfile?.services]);

  // Check subscription status
  const isSubscriptionActive = () => {
    if (!userProfile?.subscription) return false;
    
    const now = new Date();
    const endDate = userProfile.subscription.endDate?.toDate ? 
      userProfile.subscription.endDate.toDate() : 
      new Date(userProfile.subscription.endDate);
    
    return userProfile.subscription.active && endDate > now;
  };

  const isTrialExpired = () => {
    if (!userProfile?.subscription) return false;
    
    const now = new Date();
    const endDate = userProfile.subscription.endDate?.toDate ?
      userProfile.subscription.endDate.toDate() :
      new Date(userProfile.subscription.endDate);
    
    return userProfile.subscription.plan === 'trial' && endDate <= now;
  };

  const isAnySubscriptionExpired = () => {
    // Check for trial expiry
    if (isTrialExpired()) return true;
    
    // Check for paid subscription expiry
    if (userProfile?.subscriptionStatus === 'expired') return true;
    
    return false;
  };

  const getDaysRemaining = () => {
    if (!userProfile?.subscription) return 0;
    
    const now = new Date();
    const endDate = userProfile.subscription.endDate?.toDate ? 
      userProfile.subscription.endDate.toDate() : 
      new Date(userProfile.subscription.endDate);
    
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const copyPublicLink = () => {
    const link = `${window.location.origin}/salon/${currentUser.uid}`;
    navigator.clipboard.writeText(link);
    setSuccess(t('dashboard.linkCopied'));
    setTimeout(() => setSuccess(''), 3000);
  };

  const viewPublicProfile = () => {
    window.open(`/salon/${currentUser.uid}`, '_blank');
  };

  const handleQRCodeClick = () => {
    setQrModalOpen(true);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigateToTab = (tabId, section) => {
    setCurrentTab(tabId);
    setTargetSection(section);
    setProfileInfoOpen(false);
    // Clear target section after a short delay to allow component to render
    setTimeout(() => setTargetSection(null), 1000);
  };

  if (!userProfile) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: '#fafaf9'
        }}
      >
        <Box textAlign="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              border: '3px solid #e7e5e4',
              borderTop: '3px solid #1c1917',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              mx: 'auto',
              mb: 2,
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          />
          <Typography color="text.secondary">{t('dashboard.loading')}</Typography>
        </Box>
      </Box>
    );
  }

  const currentTabData = tabs.find(tab => tab.id === currentTab);

  const drawer = (
    <DashboardDrawer
      userProfile={userProfile}
      currentTab={currentTab}
      setCurrentTab={setCurrentTab}
      isMobile={isMobile}
      setMobileOpen={setMobileOpen}
      setProfileInfoOpen={setProfileInfoOpen}
      viewPublicProfile={viewPublicProfile}
      copyPublicLink={copyPublicLink}
      onQRCodeClick={handleQRCodeClick}
    />
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#fafaf9' }}>
      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar
          position="fixed"
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f6f3 50%, #f1ede8 100%)',
            color: '#1c1917',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.1)',
            borderBottom: '1px solid rgba(231, 229, 228, 0.6)',
            backdropFilter: 'blur(12px) saturate(180%)',
            WebkitBackdropFilter: 'blur(12px) saturate(180%)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)'
            }
          }}
        >
          <Toolbar sx={{ 
            px: 3, 
            minHeight: { xs: 64, sm: 70 },
            position: 'relative'
          }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                mr: 2,
                bgcolor: 'rgba(28, 25, 23, 0.04)',
                borderRadius: '12px',
                width: 44,
                height: 44,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: 'rgba(28, 25, 23, 0.08)',
                  transform: 'scale(1.05)'
                },
                '&:active': {
                  transform: 'scale(0.95)'
                }
              }}
            >
              <MenuIcon sx={{ fontSize: 24 }}/>
            </IconButton>
            
            <Box sx={{ flexGrow: 1, ml: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  lineHeight: 1.2,
                  fontSize: '1.1rem',
                  background: 'linear-gradient(135deg, #1c1917 0%, #44403c 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.02em'
                }}
              >
                {userProfile.salonName}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#78716c',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  lineHeight: 1
                }}
              >
                {currentTabData?.label}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                color="inherit"
                onClick={viewPublicProfile}
                sx={{
                  bgcolor: 'rgba(28, 25, 23, 0.04)',
                  borderRadius: '12px',
                  width: 40,
                  height: 40,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'rgba(28, 25, 23, 0.08)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  }
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 20 }}/>
              </IconButton>
              
              <IconButton
                color="inherit"
                onClick={copyPublicLink}
                sx={{
                  bgcolor: '#1c1917',
                  color: 'white',
                  borderRadius: '12px',
                  width: 40,
                  height: 40,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: '#292524',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(28, 25, 23, 0.3)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  }
                }}
              >
                <CopyIcon sx={{ fontSize: 20 }}/>
              </IconButton>
              
              <IconButton
                color="inherit"
                onClick={async () => {
                  try {
                    await logout();
                    navigate('/');
                  } catch (error) {
                    console.error('Logout error:', error);
                  }
                }}
                sx={{
                  bgcolor: 'rgba(220, 38, 38, 0.1)',
                  color: '#dc2626',
                  borderRadius: '12px',
                  width: 40,
                  height: 40,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    bgcolor: 'rgba(220, 38, 38, 0.2)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  }
                }}
              >
                <LogoutIcon sx={{ fontSize: 20 }}/>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { lg: DRAWER_WIDTH }, flexShrink: { lg: 0 } }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              bgcolor: 'white',
              borderRight: '1px solid #e7e5e4',
              boxShadow: isMobile ? 3 : 'none'
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { lg: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: isMobile ? 8 : 0
        }}
      >
        {/* Desktop Header */}
        {!isMobile && (
          <Paper
            elevation={0}
            sx={{
              bgcolor: '#f9f5f1',
              borderRadius: 3,
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              px: { xs: 2, md: 4 },
              py: { xs: 2, md: 3 }
            }}
          >
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: 2, md: 0 }
            }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1c1917', mb: { xs: 1, md: 0 } }}>
                  {currentTabData?.label}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t('dashboard.manageSalonBookings')}
                </Typography>
              </Box>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={viewPublicProfile}
                  sx={{
                    borderColor: '#d6d3d1',
                    color: '#57534e',
                    borderRadius: '50px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#a8a29e',
                      bgcolor: '#f5f5f4'
                    }
                  }}
                >
                  {t('dashboard.viewMyProfile')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CopyIcon />}
                  onClick={copyPublicLink}
                  sx={{
                    bgcolor: '#1c1917',
                    borderRadius: '50px',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#292524' }
                  }}
                >
                  {t('dashboard.copyLink')}
                </Button>
              </Stack>
            </Box>
          </Paper>
        )}

        {/* Content */}
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Subscription Status Alert - Only show for active trial */}
          {userProfile.subscription?.plan === 'trial' && !isTrialExpired() && (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{
                mb: 3,
                bgcolor: '#fffbeb',
                border: '1px solid #fed7aa',
                '& .MuiAlert-message': { width: '100%' }
              }}
              action={
                <Button
                  size="small"
                  onClick={() => setCurrentTab(3)}
                  sx={{
                    color: '#d97706',
                    fontWeight: 600
                  }}
                >
                  {t('dashboard.subscribe')}
                </Button>
              }
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t('dashboard.trialRemaining', {
                    days: getDaysRemaining(),
                    plural: getDaysRemaining() > 1 ? 's' : ''
                  })}
                </Typography>
                <Typography variant="body2">
                  {t('dashboard.subscribeForFeatures')}
                </Typography>
              </Box>
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert 
              severity="success" 
              onClose={() => setSuccess('')}
              sx={{ 
                mb: 3,
                bgcolor: '#f0fdf4',
                border: '1px solid #bbf7d0'
              }}
            >
              {success}
            </Alert>
          )}

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              onClose={() => setError('')}
              sx={{ 
                mb: 3,
                bgcolor: '#fef2f2',
                border: '1px solid #fecaca'
              }}
            >
              {error}
            </Alert>
          )}

          {/* Tab Content */}
          <Paper 
            elevation={0}
            sx={{ 
              bgcolor: 'white',
              border: '1px solid #e7e5e4',
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            <Box sx={{ p: { xs: 3, md: 4 } }}>
              {currentTab === 0 && (
                <ProfileTab
                  userProfile={userProfile}
                  setSuccess={setSuccess}
                  setError={setError}
                  targetSection={targetSection}
                  centralServices={centralServices}
                  setCentralServices={setCentralServices}
                />
              )}
              {currentTab === 1 && (
                <ScheduleTab
                  userProfile={userProfile}
                  setSuccess={setSuccess}
                  setError={setError}
                  centralServices={centralServices}
                  setCentralServices={setCentralServices}
                />
              )}
              {currentTab === 2 && (
                <BookingsTab
                  userProfile={userProfile}
                  setSuccess={setSuccess}
                  setError={setError}
                />
              )}
              {currentTab === 3 && (
                <SubscriptionTab
                  userProfile={userProfile}
                  setSuccess={setSuccess}
                  setError={setError}
                />
              )}
              {currentTab === 4 && (
                <SettingsTab
                  userProfile={userProfile}
                  setSuccess={setSuccess}
                  setError={setError}
                />
              )}
              {currentTab === 5 && (
                <SupportTab
                  userProfile={userProfile}
                  setSuccess={setSuccess}
                  setError={setError}
                />
              )}
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Profile Info Modal */}
      <ProfileInfoModal
        open={profileInfoOpen}
        onClose={() => setProfileInfoOpen(false)}
        userProfile={userProfile}
        onNavigateToTab={handleNavigateToTab}
      />

      {/* QR Code Modal */}
      <QRCodeModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        salonUrl={`${window.location.origin}/salon/${currentUser?.uid}`}
        salonName={userProfile?.salonName}
        onCopyToClipboard={copyPublicLink}
      />

      {/* Subscription Expiry Modal - Don't show on subscription tab */}
      <SubscriptionExpiryModal
        open={isAnySubscriptionExpired() && currentTab !== 3}
        userProfile={userProfile}
      />
    </Box>
  );
};

export default Dashboard;
